import React, { useState, useEffect, useContext, useMemo } from "react";
import Select from "react-select";
import "./Estimate.css";
import { UserContext } from "../../../../contexts/CurrentUserContext";
import {
  getSketchDesignEstimate,
  addSketchDesignEstimateWorkType,
  updateSketchDesignEstimateWorkType,
  deleteSketchDesignEstimateWorkType,
  getSketchDesignEstimateWorkTypeNames,
  getSketchDesignFinJobTypes,
  updateSketchDesignEstimate,
  getSketchDesignApproval,
  createSketchDesignApproval,
  deleteSketchDesignApproval,
  updateSketchDesignApproverStatus,
} from "../../../../api/projectEstimatesApi";
import { getSketchDesignProjectById } from "../../../../api/projectApi";
import CommercialOfferApprovalModal from "../../ProjectFinance/CommercialOffer/CommercialOfferApprovalModal";
import Preloader from "../../../Preloader/Preloader";
import CoinIcon from "../../../../images/projects/ProjectFinance/Coinssvg.svg";
import CalcIcon from "../../../../images/projects/ProjectFinance/Calc.svg";
import SigmaIcon from "../../../../images/projects/ProjectFinance/Sigma1.svg";

const CREATE_NEW_OPTION = "__create_new__";

const normalizeSketchWorkTypeNameEntries = (raw) => {
  const list = Array.isArray(raw) ? raw : [];
  return list.map((item) => {
    if (typeof item === "string") {
      return { name: item, work_fin_job_type_id: null };
    }
    return {
      id: item?.id,
      name: item?.name ?? "",
      work_fin_job_type_id: item?.work_fin_job_type_id,
    };
  });
};

/** Для API добавления вида работ нужен fact_stream_id; в ответах может прийти внутренний id строки справочника. */
const resolveFactStreamIdForFinJob = (finTypes, raw) => {
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return null;
  if (!Array.isArray(finTypes) || finTypes.length === 0) return n;
  const byInternalId = finTypes.find((t) => Number(t.id) === n);
  if (byInternalId != null) {
    const fs = Number(byInternalId.fact_stream_id);
    if (Number.isFinite(fs) && fs > 0) return fs;
  }
  const byFs = finTypes.find((t) => Number(t.fact_stream_id) === n);
  if (byFs != null) {
    const fs = Number(byFs.fact_stream_id);
    if (Number.isFinite(fs) && fs > 0) return fs;
  }
  return n;
};

const selectStyles = {
  container: (base) => ({ ...base, minWidth: 260 }),
  control: (base, state) => ({
    ...base,
    minHeight: "40px",
    borderColor: state.isFocused ? "#1976d2" : "#dadce0",
    boxShadow: state.isFocused ? "0 0 0 1px #1976d2" : "none",
    "&:hover": {
      borderColor: "#1976d2",
    },
  }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: "#e3f2fd",
    borderRadius: "4px",
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: "#1976d2",
  }),
  multiValueRemove: (base) => ({
    ...base,
    "&:hover": {
      backgroundColor: "#1976d2",
      color: "white",
    },
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? "#e3f2fd"
      : state.isFocused
        ? "#f5f5f5"
        : "white",
    color: "#202124",
    "&:active": {
      backgroundColor: "#e3f2fd",
    },
  }),
  menu: (base) => ({
    ...base,
    zIndex: 10,
  }),
};

const selectStylesRowFin = {
  ...selectStyles,
  container: (base) => ({
    ...base,
    minWidth: 0,
    width: "100%",
  }),
  control: (base, state) => ({
    ...selectStyles.control(base, state),
    minHeight: "48px",
    cursor: "pointer",
    backgroundColor: "#fff",
  }),
  valueContainer: (base) => ({
    ...base,
    paddingTop: 6,
    paddingBottom: 6,
  }),
  placeholder: (base) => ({
    ...base,
    color: "#80868b",
  }),
  dropdownIndicator: (base) => ({
    ...base,
    color: "#5f6368",
  }),
};

/** Выпадающий список рендерится в body, чтобы не обрезался overflow и был выше оверлея */
const ADD_WORK_TYPE_MODAL_MENU_Z = 10100;

const withAddModalSelectPortal = (baseSelectStyles) => ({
  ...baseSelectStyles,
  menuPortal: (base) => ({ ...base, zIndex: ADD_WORK_TYPE_MODAL_MENU_Z }),
  menu: (base, state) => {
    const prev =
      typeof baseSelectStyles.menu === "function"
        ? baseSelectStyles.menu(base, state)
        : base;
    return { ...prev, zIndex: ADD_WORK_TYPE_MODAL_MENU_Z };
  },
});

const selectStylesAddModalMulti = withAddModalSelectPortal(selectStyles);
const selectStylesAddModalRowFin = withAddModalSelectPortal(selectStylesRowFin);
const selectStylesAddModalSingleFin = withAddModalSelectPortal(selectStyles);

const selectMenuPortalTarget =
  typeof document !== "undefined" ? document.body : null;

const formatMoney = (value) =>
  (value ?? 0).toLocaleString("ru-RU", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const getApprovalLockMessage = (
  estimateOnApproval,
  commercialOfferOnApproval,
) => {
  if (estimateOnApproval && commercialOfferOnApproval) {
    return "Редактирование запрещено, так как смета и коммерческое предложение находятся на этапе согласования";
  }
  if (estimateOnApproval) {
    return "Редактирование запрещено, так как смета находится на этапе согласования";
  }
  if (commercialOfferOnApproval) {
    return "Редактирование запрещено, так как коммерческое предложение находится на этапе согласования";
  }
  return null;
};

const Estimate = ({
  projectId,
  projectLockedByApproval = false,
  estimateOnApproval = false,
  commercialOfferOnApproval = false,
  onApprovalStateChange,
}) => {
  const { userData } = useContext(UserContext);
  const isEditable = !projectLockedByApproval;
  const lockBannerMessage = getApprovalLockMessage(
    estimateOnApproval,
    commercialOfferOnApproval,
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [estimateNumber, setEstimateNumber] = useState("001");
  const [estimateDate, setEstimateDate] = useState(() => {
    const d = new Date();
    return d.toISOString().slice(0, 10);
  });
  const [editingEstimateNumber, setEditingEstimateNumber] = useState(false);
  const [editingEstimateDate, setEditingEstimateDate] = useState(false);
  const [addWorkTypeModalOpen, setAddWorkTypeModalOpen] = useState(false);
  const [addWorkTypeSelected, setAddWorkTypeSelected] = useState([]);
  const [addWorkTypeNewName, setAddWorkTypeNewName] = useState("");
  const [addWorkTypeSubmitting, setAddWorkTypeSubmitting] = useState(false);
  const [workTypeNamesList, setWorkTypeNamesList] = useState([]);
  const [workTypeNamesLoading, setWorkTypeNamesLoading] = useState(false);
  const [finJobTypesList, setFinJobTypesList] = useState([]);
  const [addWorkTypeFinJobType, setAddWorkTypeFinJobType] = useState(null);
  const [addWorkTypeLineFinJob, setAddWorkTypeLineFinJob] = useState({});
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [approvalData, setApprovalData] = useState(null);
  const [projectData, setProjectData] = useState(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [isSubmittingApproval, setIsSubmittingApproval] = useState(false);
  const [isDeletingApproval, setIsDeletingApproval] = useState(false);
  const [isApproverActionLoading, setIsApproverActionLoading] = useState(false);
  const [directThresholdPercentApplied, setDirectThresholdPercentApplied] =
    useState("40");
  const [directThresholdPercentDraft, setDirectThresholdPercentDraft] =
    useState("40");
  const [isDirectThresholdEditing, setIsDirectThresholdEditing] =
    useState(false);
  const [isDirectThresholdSubmitting, setIsDirectThresholdSubmitting] =
    useState(false);

  const isAddWorkTypeCreateNew =
    addWorkTypeSelected.length === 1 &&
    addWorkTypeSelected[0]?.value === CREATE_NEW_OPTION;

  const finJobTypeSelectOptions = useMemo(
    () =>
      (finJobTypesList || []).map((t) => {
        const fs = Number(t.fact_stream_id);
        const value =
          Number.isFinite(fs) && fs > 0 ? fs : Number(t.id);
        return {
          value,
          label: t.name || String(value),
        };
      }),
    [finJobTypesList],
  );

  const refetchEstimate = async () => {
    if (!projectId) return;
    try {
      const result = await getSketchDesignEstimate(projectId);
      setData(result);
    } catch (err) {
      console.error("Ошибка обновления сметы:", err);
    }
  };

  useEffect(() => {
    const fetchEstimate = async () => {
      if (!projectId) {
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const [result, approval, project] = await Promise.all([
          getSketchDesignEstimate(projectId),
          getSketchDesignApproval(projectId, "estimate"),
          getSketchDesignProjectById(projectId),
        ]);
        setData(result);
        setApprovalData(approval ?? null);
        setProjectData(project ?? null);
      } catch (err) {
        console.error("Ошибка загрузки сметы:", err);
        setError(err?.message || "Не удалось загрузить смету");
      } finally {
        setLoading(false);
      }
    };
    fetchEstimate();
  }, [projectId]);

  /** fin_job_type / work_fin_job_type_id по имени из уже добавленных в проект видов работ */
  const finJobRawIdByExistingWorkTypeName = useMemo(() => {
    const m = new Map();
    for (const row of data?.work_types ?? []) {
      const key = (row.name || "").trim().toLowerCase();
      if (!key) continue;
      const raw = row.fin_job_type ?? row.work_fin_job_type_id;
      const n = Number(raw);
      if (Number.isFinite(n) && n > 0) m.set(key, n);
    }
    return m;
  }, [data?.work_types]);

  /** work_fin_job_type_id из ответа GET …/work_types/names по имени */
  const rawFinJobIdByCatalogNameFromApi = useMemo(() => {
    const m = new Map();
    for (const w of workTypeNamesList) {
      const key = (w.name || "").trim().toLowerCase();
      if (!key) continue;
      const n = Number(w.work_fin_job_type_id);
      if (Number.isFinite(n) && n > 0) m.set(key, n);
    }
    return m;
  }, [workTypeNamesList]);

  const total = data?.contract_amount_total ?? 0;
  const directExpenses = data?.direct_expenses ?? 0;
  const clearProfit = data?.clear_profit ?? 0;
  const maxDirectExpenses = data?.max_direct_expenses ?? 0;
  const withoutNds = data?.contract_amount_total_without_nds ?? 0;
  const currentNds = data?.current_nds ?? 0;
  const ndsSum = total - withoutNds;

  const directPercent =
    total > 0 ? ((directExpenses / total) * 100).toFixed(0) : "0";
  const profitPercent =
    total > 0 ? ((clearProfit / total) * 100).toFixed(0) : "0";
  const maxDirectPercentage = data?.max_direct_percentage ?? 40;

  useEffect(() => {
    if (!Number.isFinite(maxDirectPercentage)) {
      setDirectThresholdPercentApplied("40");
      setDirectThresholdPercentDraft("40");
      return;
    }
    const normalized = Math.round(Math.max(maxDirectPercentage, 0) * 100) / 100;
    const formatted = String(normalized);
    setDirectThresholdPercentApplied(formatted);
    setDirectThresholdPercentDraft(formatted);
  }, [maxDirectPercentage]);

  useEffect(() => {
    if (!addWorkTypeModalOpen) return;
    if (isAddWorkTypeCreateNew) {
      setAddWorkTypeLineFinJob({});
      return;
    }
    const opts = addWorkTypeSelected.filter(
      (o) => o?.value !== CREATE_NEW_OPTION,
    );
    setAddWorkTypeLineFinJob((prev) => {
      const next = { ...prev };
      const keys = new Set(opts.map((o) => String(o.value)));
      for (const k of Object.keys(next)) {
        if (!keys.has(k)) delete next[k];
      }

      const resolveRawForOption = (o) => {
        let wid = o.workFinJobTypeId;
        if (Number.isFinite(wid) && wid > 0) return wid;
        const key = String(o.value || "").trim().toLowerCase();
        if (!key) return null;
        return (
          rawFinJobIdByCatalogNameFromApi.get(key) ??
          finJobRawIdByExistingWorkTypeName.get(key) ??
          null
        );
      };

      for (const o of opts) {
        const nameKey = String(o.value);
        const cur = next[nameKey];
        if (cur && Number.isFinite(cur.value) && cur.value > 0) continue;
        const raw = resolveRawForOption(o);
        const fs =
          raw != null && Number.isFinite(raw) && raw > 0
            ? resolveFactStreamIdForFinJob(finJobTypesList, raw)
            : null;
        if (fs != null && fs > 0) {
          const row = finJobTypesList.find(
            (t) => Number(t.fact_stream_id) === fs,
          );
          next[nameKey] = { value: fs, label: row?.name ?? String(fs) };
        } else if (!(nameKey in next)) {
          next[nameKey] = null;
        }
      }
      return next;
    });
  }, [
    addWorkTypeModalOpen,
    isAddWorkTypeCreateNew,
    addWorkTypeSelected,
    finJobTypesList,
    rawFinJobIdByCatalogNameFromApi,
    finJobRawIdByExistingWorkTypeName,
  ]);

  const parsePercentValue = (rawValue) => {
    const parsed = Number.parseFloat(String(rawValue).replace(",", "."));
    if (!Number.isFinite(parsed)) return 0;
    return Math.max(parsed, 0);
  };

  const formatPercentDisplay = (value) => {
    if (!Number.isFinite(value)) return "0";
    if (Number.isInteger(value)) return String(value);
    return value.toFixed(2).replace(/\.?0+$/, "");
  };

  const thresholdPercentValue = parsePercentValue(
    directThresholdPercentApplied,
  );
  const canEditDirectThreshold =
    isEditable &&
    userData?.access_levels?.some((level) =>
      ["РО", "ФИН", "АДМС"].includes(level?.name),
    );

  const formatDisplayDate = (isoDate) => {
    if (!isoDate) return "";
    const [y, m, d] = isoDate.split("-");
    return `${d}.${m}.${y}`;
  };

  if (!projectId) {
    return (
      <div className="sd-estimate">
        <div className="sd-estimate-error">Ошибка: ID проекта не передан</div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="sd-estimate">
        <Preloader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="sd-estimate">
        <div className="sd-estimate-error">Ошибка: {error}</div>
      </div>
    );
  }

  const workTypes = data?.work_types ?? [];
  const workTypesSorted = [...workTypes].sort(
    (a, b) => (a.id ?? 0) - (b.id ?? 0),
  );
  const existingNames = workTypes
    .map((w) => (w.name || "").trim().toLowerCase())
    .filter(Boolean);

  const availableWorkTypeOptions = workTypeNamesList
    .filter((w) => !existingNames.includes((w.name || "").trim().toLowerCase()))
    .map((w) => {
      const key = (w.name || "").trim().toLowerCase();
      const rawId =
        rawFinJobIdByCatalogNameFromApi.get(key) ??
        finJobRawIdByExistingWorkTypeName.get(key) ??
        null;
      return {
        value: w.name,
        label: w.name,
        workFinJobTypeId:
          rawId != null && Number.isFinite(rawId) && rawId > 0 ? rawId : null,
      };
    });

  const getResolvedFactStreamForCatalogRow = (opt) => {
    const nameKey = String(opt?.value ?? "");
    const lineSel = addWorkTypeLineFinJob[nameKey];
    if (
      lineSel &&
      Number.isFinite(lineSel.value) &&
      lineSel.value > 0
    ) {
      return lineSel.value;
    }
    const key = nameKey.trim().toLowerCase();
    const wid =
      opt?.workFinJobTypeId != null &&
      Number.isFinite(opt.workFinJobTypeId) &&
      opt.workFinJobTypeId > 0
        ? opt.workFinJobTypeId
        : rawFinJobIdByCatalogNameFromApi.get(key) ??
          finJobRawIdByExistingWorkTypeName.get(key);
    if (Number.isFinite(wid) && wid > 0) {
      const mapped = resolveFactStreamIdForFinJob(finJobTypesList, wid);
      if (mapped != null && mapped > 0) return mapped;
    }
    return null;
  };

  const catalogSelectionForAdd = addWorkTypeSelected.filter(
    (opt) => opt?.value !== CREATE_NEW_OPTION,
  );

  const newNameTrimmed = addWorkTypeNewName.trim();
  const isDuplicateName =
    isAddWorkTypeCreateNew &&
    newNameTrimmed &&
    existingNames.includes(newNameTrimmed.toLowerCase());

  const catalogFinOk =
    catalogSelectionForAdd.length > 0 &&
    catalogSelectionForAdd.every(
      (opt) => getResolvedFactStreamForCatalogRow(opt) != null,
    );

  const createNewFinOk =
    isAddWorkTypeCreateNew &&
    Number.isFinite(addWorkTypeFinJobType?.value) &&
    addWorkTypeFinJobType.value > 0;

  const canAddWorkType = isAddWorkTypeCreateNew
    ? newNameTrimmed && !isDuplicateName && createNewFinOk
    : catalogSelectionForAdd.length > 0 && catalogFinOk;

  const openAddModal = async () => {
    setAddWorkTypeSelected([]);
    setAddWorkTypeNewName("");
    setAddWorkTypeFinJobType(null);
    setAddWorkTypeLineFinJob({});
    setAddWorkTypeModalOpen(true);
    setWorkTypeNamesLoading(true);
    try {
      const [names, finTypes] = await Promise.all([
        getSketchDesignEstimateWorkTypeNames(),
        getSketchDesignFinJobTypes(),
      ]);
      setWorkTypeNamesList(normalizeSketchWorkTypeNameEntries(names));
      setFinJobTypesList(Array.isArray(finTypes) ? finTypes : []);
    } catch (err) {
      console.error("Ошибка загрузки списка видов работ:", err);
      setWorkTypeNamesList([]);
      setFinJobTypesList([]);
    } finally {
      setWorkTypeNamesLoading(false);
    }
  };

  const closeAddModal = () => {
    setAddWorkTypeModalOpen(false);
    setAddWorkTypeFinJobType(null);
    setAddWorkTypeLineFinJob({});
  };

  const validateNewName = () => {
    // Валидация дубликата отображается через isDuplicateName в UI
  };

  const handleAddWorkType = async () => {
    if (!isEditable || !canAddWorkType || addWorkTypeSubmitting || !projectId)
      return;

    const namesToAdd = isAddWorkTypeCreateNew
      ? [newNameTrimmed]
      : catalogSelectionForAdd.map((opt) => opt.value);

    if (namesToAdd.length === 0) return;
    if (
      isAddWorkTypeCreateNew &&
      existingNames.includes(namesToAdd[0].toLowerCase())
    )
      return;

    setAddWorkTypeSubmitting(true);
    try {
      for (const nameToAdd of namesToAdd) {
        const name = (nameToAdd || "").trim();
        const rawArticle = isAddWorkTypeCreateNew
          ? addWorkTypeFinJobType.value
          : (() => {
              const nameKey = String(name);
              const lineSel = addWorkTypeLineFinJob[nameKey];
              if (
                lineSel &&
                Number.isFinite(lineSel.value) &&
                lineSel.value > 0
              ) {
                return lineSel.value;
              }
              const opt = catalogSelectionForAdd.find((o) => o.value === name);
              const key = (name || "").trim().toLowerCase();
              const wid =
                opt?.workFinJobTypeId != null &&
                Number.isFinite(opt.workFinJobTypeId) &&
                opt.workFinJobTypeId > 0
                  ? opt.workFinJobTypeId
                  : rawFinJobIdByCatalogNameFromApi.get(key) ??
                    finJobRawIdByExistingWorkTypeName.get(key);
              if (Number.isFinite(wid) && wid > 0) return wid;
              return null;
            })();
        const work_fin_job_type_id = resolveFactStreamIdForFinJob(
          finJobTypesList,
          rawArticle,
        );
        if (!Number.isFinite(work_fin_job_type_id) || work_fin_job_type_id <= 0) {
          alert(
            `Не удалось определить статью для вида работ «${name}». Выберите статью в списке.`,
          );
          return;
        }
        await addSketchDesignEstimateWorkType(projectId, {
          name,
          work_fin_job_type_id,
          is_reserve: name.toLowerCase() === "резерв",
        });
      }
      await refetchEstimate();
      closeAddModal();
    } catch (err) {
      console.error(err);
      alert(err?.message || "Ошибка добавления вида работ");
    } finally {
      setAddWorkTypeSubmitting(false);
    }
  };

  const handleDeleteWorkType = async (id) => {
    if (!isEditable || !id || !window.confirm("Удалить вид работ?")) return;
    setActionLoadingId(id);
    try {
      await deleteSketchDesignEstimateWorkType(id);
      await refetchEstimate();
    } catch (err) {
      console.error(err);
      alert(err?.message || "Ошибка удаления");
    } finally {
      setActionLoadingId(null);
    }
  };

  const totalShare = workTypes.reduce(
    (s, w) => s + (Number(w.share_percent) || 0),
    0,
  );
  const totalMiddleSalary = workTypes.reduce(
    (s, w) => s + (Number(w.middle_salary) || 0),
    0,
  );
  const totalLabor = workTypes.reduce(
    (s, w) => s + (Number(w.labor_costs) || 0),
    0,
  );
  const totalDirect = workTypes.reduce(
    (s, w) => s + (Number(w.final_sum) || 0),
    0,
  );

  const buildWorkTypeUpdateBody = (row, overrides = {}) => ({
    name: row.name ?? "",
    middle_salary: Number(row.middle_salary) || 0,
    labor_costs: Number(row.labor_costs) || 0,
    already_payed: Number(row.already_payed) || 0,
    status: row.status ?? "in_processing",
    ...overrides,
  });

  const getStatusIcon = (status) => {
    if (!status)
      return (
        <span className="sd-estimate-approval-status-icon pending">⏳</span>
      );
    const s = String(status).toLowerCase();
    if (s === "согласован" || s === "approved")
      return (
        <span className="sd-estimate-approval-status-icon approved">✓</span>
      );
    if (s === "на рассмотрении" || s === "pending")
      return (
        <span className="sd-estimate-approval-status-icon pending">⏳</span>
      );
    if (s === "не согласован" || s === "rejected")
      return (
        <span className="sd-estimate-approval-status-icon rejected">✕</span>
      );
    return <span className="sd-estimate-approval-status-icon pending">⏳</span>;
  };

  const getStatusText = (status) => {
    if (!status) return "На рассмотрении";
    const s = String(status).toLowerCase();
    if (s === "согласован" || s === "approved") return "Согласован";
    if (s === "на рассмотрении" || s === "pending") return "На рассмотрении";
    if (s === "не согласован" || s === "rejected") return "Отклонен";
    return "На рассмотрении";
  };

  const toNumericApproverIds = (checked) =>
    checked
      .map((id) => (typeof id === "number" ? id : parseInt(id, 10)))
      .filter((id) => Number.isInteger(id) && !Number.isNaN(id));

  const handleApprovalSubmit = async (checkedIds) => {
    const ids = toNumericApproverIds(checkedIds);
    if (!projectId || ids.length === 0) return;
    setIsSubmittingApproval(true);
    try {
      await createSketchDesignApproval(projectId, "estimate", ids);
      const updated = await getSketchDesignApproval(projectId, "estimate");
      setApprovalData(updated ?? null);
      setShowApprovalModal(false);
      onApprovalStateChange?.();
      alert("Смета успешно отправлена на согласование.");
    } catch (err) {
      console.error("Ошибка при отправке сметы на согласование:", err);
      alert(err?.message || "Ошибка при отправке на согласование.");
    } finally {
      setIsSubmittingApproval(false);
    }
  };

  const handleDeleteApproval = async () => {
    if (!projectId || !window.confirm("Удалить согласование по смете?")) return;
    setIsDeletingApproval(true);
    try {
      await deleteSketchDesignApproval(projectId, "estimate");
      setApprovalData(null);
      onApprovalStateChange?.();
      alert("Согласование удалено.");
    } catch (err) {
      console.error("Ошибка при удалении согласования:", err);
      alert(err?.message || "Ошибка при удалении согласования.");
    } finally {
      setIsDeletingApproval(false);
    }
  };

  const myApprover = approvalData?.approvers?.find(
    (a) =>
      a.user?.id === userData?.id || String(a.user_id) === String(userData?.id),
  );
  const isMyApprovalPending =
    myApprover &&
    ["in_processing", "pending", "на рассмотрении"].includes(
      String(myApprover.status || "").toLowerCase(),
    );

  const handleApproverSetStatus = async (status) => {
    if (!myApprover?.id || !projectId) return;
    setIsApproverActionLoading(true);
    try {
      await updateSketchDesignApproverStatus(myApprover.id, status);
      const updated = await getSketchDesignApproval(projectId, "estimate");
      setApprovalData(updated ?? null);
      alert(status === "approved" ? "Смета согласована." : "Смета отклонена.");
    } catch (err) {
      console.error("Ошибка при обновлении статуса:", err);
      alert(err?.message || "Ошибка при обновлении статуса");
    } finally {
      setIsApproverActionLoading(false);
    }
  };

  const handleDirectThresholdChange = (e) => {
    const value = e.target.value.replace(",", ".");
    if (!/^\d*\.?\d*$/.test(value)) return;
    setDirectThresholdPercentDraft(value);
  };

  const handleDirectThresholdFocus = (e) => {
    e.target.select();
    setIsDirectThresholdEditing(true);
  };

  const handleDirectThresholdApply = async () => {
    if (!projectId) return;
    const normalizedValue = formatPercentDisplay(
      parsePercentValue(directThresholdPercentDraft),
    );
    setIsDirectThresholdSubmitting(true);
    try {
      await updateSketchDesignEstimate(projectId, {
        contract_amount_total: Number(data?.contract_amount_total) || 0,
        contract_amount_total_without_nds:
          Number(data?.contract_amount_total_without_nds) || 0,
        direct_expenses: Number(data?.direct_expenses) || 0,
        clear_profit: Number(data?.clear_profit) || 0,
        max_direct_percentage: parsePercentValue(normalizedValue),
        max_direct_expenses: Number(data?.max_direct_expenses) || 0,
        deviations: Number(data?.deviations) || 0,
      });
      await refetchEstimate();
      setDirectThresholdPercentApplied(normalizedValue);
      setDirectThresholdPercentDraft(normalizedValue);
      setIsDirectThresholdEditing(false);
    } catch (err) {
      console.error("Ошибка обновления порога прямых затрат:", err);
      alert(err?.message || "Не удалось обновить порог прямых затрат");
    } finally {
      setIsDirectThresholdSubmitting(false);
    }
  };

  const handleDirectThresholdCancel = () => {
    setDirectThresholdPercentDraft(directThresholdPercentApplied);
    setIsDirectThresholdEditing(false);
  };

  return (
    <div className="sd-estimate">
      {projectLockedByApproval && lockBannerMessage && (
        <div className="sd-estimate-lock-banner" role="alert">
          <span className="sd-estimate-lock-banner-icon">⚠</span>
          {lockBannerMessage}
        </div>
      )}
      {/* Блок 1: Результаты расчета сметы */}
      <section className="sd-estimate-block sd-estimate-block-results">
        <div className="sd-estimate-block-results-top-bar">
          <h2 className="sd-estimate-block-title">Результаты расчета сметы</h2>
          {approvalData?.approvers?.length > 0 ? (
            <div className="sd-estimate-approval-with-actions">
              <div className="sd-estimate-approval-status-block">
                {approvalData.approvers.map((approver, index) => {
                  const name = approver.user
                    ? [approver.user.surname, approver.user.name]
                        .filter(Boolean)
                        .join(" ")
                        .trim()
                    : "Не указан";
                  return (
                    <div
                      key={approver.id ?? index}
                      className="sd-estimate-approval-step"
                    >
                      <span className="sd-estimate-approver-name">
                        {name || "Не указан"}:
                      </span>
                      {getStatusIcon(approver.status)}
                      <span className="sd-estimate-step-status-text">
                        {getStatusText(approver.status)}
                      </span>
                    </div>
                  );
                })}
              </div>
              <button
                type="button"
                className="sd-estimate-btn-delete-approval"
                onClick={handleDeleteApproval}
                disabled={isDeletingApproval}
              >
                {isDeletingApproval ? "Удаление..." : "Удалить согласование"}
              </button>
            </div>
          ) : (
            <div className="sd-estimate-approval-empty-row">
              <div className="sd-estimate-approval-empty">
                <span className="sd-estimate-approval-empty-icon">ℹ️</span>
                Согласований по смете пока нет
              </div>
              <button
                type="button"
                className="sd-estimate-btn-send-approval"
                onClick={() => setShowApprovalModal(true)}
                disabled={isSubmittingApproval}
              >
                {isSubmittingApproval
                  ? "Отправка..."
                  : "Отправить на согласование"}
              </button>
            </div>
          )}
        </div>
        <div className="sd-estimate-inputs-row">
          <div className="sd-estimate-input-group">
            <label>№ сметы</label>
            {editingEstimateNumber && isEditable ? (
              <span className="sd-estimate-editable-row">
                <input
                  type="text"
                  value={estimateNumber}
                  onChange={(e) => setEstimateNumber(e.target.value)}
                  onBlur={() => setEditingEstimateNumber(false)}
                  onKeyDown={(e) =>
                    e.key === "Enter" && setEditingEstimateNumber(false)
                  }
                  className="sd-estimate-input"
                  autoFocus
                />
                <button
                  type="button"
                  className="sd-estimate-edit-done"
                  onClick={() => setEditingEstimateNumber(false)}
                  aria-label="Готово"
                >
                  ✓
                </button>
              </span>
            ) : (
              <span className="sd-estimate-display-row">
                <span className="sd-estimate-display-value">
                  {estimateNumber}
                </span>
                {isEditable && (
                  <button
                    type="button"
                    className="sd-estimate-pencil"
                    onClick={() => setEditingEstimateNumber(true)}
                    title="Редактировать"
                    aria-label="Редактировать"
                  >
                    ✎
                  </button>
                )}
              </span>
            )}
          </div>
          <div className="sd-estimate-input-group">
            <label>Введите дату</label>
            {editingEstimateDate && isEditable ? (
              <span className="sd-estimate-editable-row">
                <input
                  type="date"
                  value={estimateDate}
                  onChange={(e) => setEstimateDate(e.target.value)}
                  onBlur={() => setEditingEstimateDate(false)}
                  className="sd-estimate-input"
                  autoFocus
                />
                <button
                  type="button"
                  className="sd-estimate-edit-done"
                  onClick={() => setEditingEstimateDate(false)}
                  aria-label="Готово"
                >
                  ✓
                </button>
              </span>
            ) : (
              <span className="sd-estimate-display-row">
                <span className="sd-estimate-date-icon" aria-hidden>
                  📅
                </span>
                <span className="sd-estimate-display-value">
                  {formatDisplayDate(estimateDate)}
                </span>
                {isEditable && (
                  <button
                    type="button"
                    className="sd-estimate-pencil"
                    onClick={() => setEditingEstimateDate(true)}
                    title="Редактировать"
                    aria-label="Редактировать"
                  >
                    ✎
                  </button>
                )}
              </span>
            )}
          </div>
        </div>
      </section>

      {/* Блок 2: План рентабельности — четыре карточки */}
      <section className="sd-estimate-block sd-estimate-block-plan">
        <h2 className="sd-estimate-block-title">План рентабельности проекта</h2>
        <div className="sd-estimate-cards-row">
          <div className="sd-estimate-card">
            <div className="sd-estimate-card-icon sd-estimate-card-icon-blue">
              <img src={CoinIcon} alt="" />
            </div>
            <div className="sd-estimate-card-label">Доход</div>
            <div className="sd-estimate-card-value">
              〒 {formatMoney(total)}
            </div>
            <div className="sd-estimate-card-sub">100%</div>
          </div>
          <div className="sd-estimate-card">
            <div className="sd-estimate-card-icon sd-estimate-card-icon-yellow">
              <img src={CalcIcon} alt="" />
            </div>
            <div className="sd-estimate-card-label">Расходы прямые</div>
            <div className="sd-estimate-card-value">
              〒 {formatMoney(directExpenses)}
            </div>
            <div className="sd-estimate-card-sub">{directPercent}%</div>
          </div>
          <div className="sd-estimate-card">
            <div className="sd-estimate-card-icon sd-estimate-card-icon-green">
              <img src={SigmaIcon} alt="" />
            </div>
            <div className="sd-estimate-card-label">Валовая прибыль</div>
            <div className="sd-estimate-card-value">
              〒 {formatMoney(clearProfit)}
            </div>
            <div className="sd-estimate-card-sub">{profitPercent}%</div>
          </div>
          {/* <div className="sd-estimate-card">
            <div className="sd-estimate-card-icon sd-estimate-card-icon-target">
              <img src={TargetIcon} alt="" />
            </div>
            <div className="sd-estimate-card-label">
              Нормативная чистая прибыль за отделение
            </div>
            <div className="sd-estimate-card-value">
              〒 {formatMoney(clearProfit)}
            </div>
            <div className="sd-estimate-card-sub">%14</div>
          </div> */}
        </div>

        {/* Два блока сумм */}
        <div className="sd-estimate-summary-blocks">
          <div className="sd-estimate-summary-block">
            <div className="sd-estimate-summary-block-label">
              СУММА ОСНОВНОГО ДОГОВОРА
            </div>
            <div className="sd-estimate-summary-block-value">
              〒 {formatMoney(total)}
            </div>
          </div>
          <div className="sd-estimate-summary-block">
            <div className="sd-estimate-summary-block-label">
              СУММА ЗА ВЫЧЕТОМ НДС {currentNds}%
            </div>
            <div className="sd-estimate-summary-block-value">
              〒 {formatMoney(withoutNds)}
            </div>
            <div className="sd-estimate-summary-block-nds">
              Сумма НДС: {formatMoney(ndsSum)} 〒
            </div>
          </div>
        </div>

        {/* Список: порог, затраты, отклонения */}
        <ul className="sd-estimate-metrics-list">
          <li className="sd-estimate-metrics-item">
            <span className="sd-estimate-metrics-bullet sd-estimate-metrics-bullet-blue" />
            <span className="sd-estimate-metrics-label">
              Порог прямых затрат
            </span>
            <span className="sd-estimate-metrics-threshold-control">
              {canEditDirectThreshold ? (
                <>
                  <input
                    type="text"
                    inputMode="decimal"
                    className="sd-estimate-threshold-input"
                    value={directThresholdPercentDraft}
                    onChange={handleDirectThresholdChange}
                    onFocus={handleDirectThresholdFocus}
                    aria-label="Процент порога прямых затрат"
                  />
                  <span className="sd-estimate-threshold-unit">%</span>
                  {isDirectThresholdEditing && (
                    <span
                      className="sd-estimate-threshold-actions"
                      role="group"
                    >
                      <button
                        type="button"
                        className="sd-estimate-threshold-action sd-estimate-threshold-action-apply"
                        onClick={handleDirectThresholdApply}
                        disabled={isDirectThresholdSubmitting}
                        title="Применить"
                        aria-label="Применить"
                      >
                        ✓
                      </button>
                      <button
                        type="button"
                        className="sd-estimate-threshold-action sd-estimate-threshold-action-cancel"
                        onClick={handleDirectThresholdCancel}
                        disabled={isDirectThresholdSubmitting}
                        title="Отменить"
                        aria-label="Отменить"
                      >
                        ✕
                      </button>
                    </span>
                  )}
                </>
              ) : (
                <span className="sd-estimate-threshold-readonly">
                  ({formatPercentDisplay(maxDirectPercentage)}%)
                </span>
              )}
            </span>
            <span className="sd-estimate-metrics-value">
              〒 {formatMoney(maxDirectExpenses)}
            </span>
          </li>
          <li className="sd-estimate-metrics-item">
            <span className="sd-estimate-metrics-bullet sd-estimate-metrics-bullet-green" />
            <span className="sd-estimate-metrics-label">
              Рассчитываемые затраты (ПЗ)
            </span>
            <span className="sd-estimate-metrics-value">
              〒 {formatMoney(directExpenses)}
            </span>
          </li>
          <li className="sd-estimate-metrics-item">
            <span className="sd-estimate-metrics-bullet sd-estimate-metrics-bullet-green" />
            <span className="sd-estimate-metrics-label">Отклонения (+,-)</span>
            <span
              className={`sd-estimate-metrics-value sd-estimate-deviation ${(data?.deviations ?? 0) >= 0 ? "sd-estimate-deviation-plus" : "sd-estimate-deviation-minus"}`}
            >
              〒 {(data?.deviations ?? 0) >= 0 ? "+" : ""}
              {formatMoney(data?.deviations ?? 0)}
            </span>
          </li>
        </ul>
      </section>

      {/* Блок 3: Виды работ */}
      <section className="sd-estimate-block sd-estimate-block-work-types">
        <div className="sd-estimate-work-types-header">
          <h2 className="sd-estimate-block-title">Вид работ</h2>
          <button
            type="button"
            className="sd-estimate-btn-add"
            onClick={openAddModal}
            disabled={!isEditable}
          >
            <span className="sd-estimate-btn-add-icon">+</span>
            Добавить вид работ
          </button>
        </div>
        <div className="sd-estimate-table-wrap">
          <table className="sd-estimate-table">
            <thead>
              <tr>
                <th>Вид работ</th>
                <th>
                  % доля в ЭП{" "}
                  <span
                    className="sd-estimate-th-info"
                    title="Доля в эскизном проекте"
                  >
                    ?
                  </span>
                </th>
                <th>
                  Средняя зарплата{" "}
                  <span
                    className="sd-estimate-th-info"
                    title="Средняя зарплата"
                  >
                    ?
                  </span>
                </th>
                <th>
                  Трудозатраты (ч.){" "}
                  <span className="sd-estimate-th-info" title="Часы">
                    ?
                  </span>
                </th>
                <th>
                  Прямые затраты{" "}
                  <span className="sd-estimate-th-info" title="Прямые затраты">
                    ?
                  </span>
                </th>
                <th className="sd-estimate-th-actions">Действия</th>
              </tr>
            </thead>
            <tbody>
              {workTypesSorted.length > 0 ? (
                <>
                  {workTypesSorted.map((row) => (
                    <tr key={row.id}>
                      <td className="sd-estimate-cell-text">
                        {row.name ?? "—"}
                      </td>
                      <td>
                        <span className="sd-estimate-cell-value">
                          {row.share_percent != null
                            ? `${Number(row.share_percent).toLocaleString("ru-RU")}%`
                            : "—"}
                        </span>
                      </td>
                      <td>
                        <span className="sd-estimate-cell-edit">
                          <span className="sd-estimate-cell-currency">〒</span>
                          <input
                            type="text"
                            defaultValue={
                              row.middle_salary != null ? row.middle_salary : ""
                            }
                            className="sd-estimate-cell-input"
                            readOnly={!isEditable}
                            onFocus={(e) => {
                              if (isEditable) {
                                const input = e.target;
                                requestAnimationFrame(() => input.select());
                              }
                            }}
                            onBlur={(e) => {
                              if (!isEditable) return;
                              const v = e.target.value
                                .replace(/\s/g, "")
                                .replace(",", ".");
                              const num = v === "" ? 0 : parseFloat(v);
                              if (!Number.isNaN(num) && num >= 0 && row.id) {
                                updateSketchDesignEstimateWorkType(
                                  row.id,
                                  buildWorkTypeUpdateBody(row, {
                                    middle_salary: num,
                                  }),
                                )
                                  .then(refetchEstimate)
                                  .catch(console.error);
                              }
                            }}
                          />
                          {isEditable && (
                            <button
                              type="button"
                              className="sd-estimate-pencil"
                              aria-label="Редактировать"
                            >
                              ✎
                            </button>
                          )}
                        </span>
                      </td>
                      <td>
                        <span className="sd-estimate-cell-edit">
                          <input
                            type="text"
                            defaultValue={
                              row.labor_costs != null ? row.labor_costs : ""
                            }
                            className="sd-estimate-cell-input"
                            readOnly={!isEditable}
                            onFocus={(e) => {
                              if (isEditable) {
                                const input = e.target;
                                requestAnimationFrame(() => input.select());
                              }
                            }}
                            onBlur={(e) => {
                              if (!isEditable) return;
                              const v = e.target.value;
                              const num =
                                v === "" ? 0 : parseFloat(v.replace(",", "."));
                              if (!Number.isNaN(num) && num >= 0 && row.id) {
                                updateSketchDesignEstimateWorkType(
                                  row.id,
                                  buildWorkTypeUpdateBody(row, {
                                    labor_costs: num,
                                  }),
                                )
                                  .then(refetchEstimate)
                                  .catch(console.error);
                              }
                            }}
                          />
                          {isEditable && (
                            <button
                              type="button"
                              className="sd-estimate-pencil"
                              aria-label="Редактировать"
                            >
                              ✎
                            </button>
                          )}
                        </span>
                      </td>
                      <td>
                        <span className="sd-estimate-cell-value">
                          〒 {formatMoney(row.final_sum ?? 0)}
                        </span>
                      </td>
                      <td className="sd-estimate-cell-actions">
                        <button
                          type="button"
                          className="sd-estimate-btn-delete"
                          aria-label="Удалить"
                          onClick={() => handleDeleteWorkType(row.id)}
                          disabled={!isEditable || actionLoadingId === row.id}
                        >
                          🗑
                        </button>
                      </td>
                    </tr>
                  ))}
                  <tr className="sd-estimate-table-total">
                    <td>
                      <strong>ИТОГО</strong>
                    </td>
                    <td>
                      <strong>{totalShare.toLocaleString("ru-RU")}%</strong>
                    </td>
                    <td>
                      <strong>〒 {formatMoney(totalMiddleSalary)}</strong>
                    </td>
                    <td>
                      <strong>{totalLabor.toLocaleString("ru-RU")}</strong>
                    </td>
                    <td>
                      <strong>〒 {formatMoney(totalDirect)}</strong>
                    </td>
                    <td />
                  </tr>
                </>
              ) : (
                <tr>
                  <td colSpan={6} className="sd-estimate-table-empty">
                    Нет видов работ. Нажмите «Добавить вид работ».
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Модальное окно добавления вида работ */}
      {addWorkTypeModalOpen && (
        <div className="sd-estimate-modal-overlay" onClick={closeAddModal}>
          <div
            className="sd-estimate-modal sd-estimate-modal--wide-add-work-types"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sd-estimate-modal-scroll">
            <h3 className="sd-estimate-modal-title">
              Добавление нового вида работ
            </h3>
            <div className="sd-estimate-modal-field">
              <label>Выберите виды работ из списка*</label>
              {workTypeNamesLoading ? (
                <div className="sd-estimate-modal-loading">Загрузка...</div>
              ) : (
                <>
                  {availableWorkTypeOptions.length === 0 &&
                    workTypeNamesList.length === 0 && (
                      <div className="sd-estimate-modal-all-added">
                        Список видов работ с сервера пуст. Выберите «Создать
                        новый вид работ» ниже и введите название.
                      </div>
                    )}
                  {availableWorkTypeOptions.length === 0 &&
                    workTypeNamesList.length > 0 && (
                      <div className="sd-estimate-modal-all-added">
                        Все доступные виды работ уже добавлены в проект. Можете
                        создать новый вид работ ниже.
                      </div>
                    )}
                  <Select
                    isMulti
                    options={[
                      ...availableWorkTypeOptions,
                      {
                        value: CREATE_NEW_OPTION,
                        label: "+ Создать новый вид работ",
                      },
                    ]}
                    value={addWorkTypeSelected}
                    onChange={(selectedOptions) => {
                      const selected = selectedOptions || [];
                      const hasCreateNew = selected.some(
                        (opt) => opt.value === CREATE_NEW_OPTION,
                      );
                      if (hasCreateNew) {
                        setAddWorkTypeSelected([
                          {
                            value: CREATE_NEW_OPTION,
                            label: "+ Создать новый вид работ",
                          },
                        ]);
                      } else {
                        setAddWorkTypeSelected(selected);
                      }
                    }}
                    isLoading={workTypeNamesLoading}
                    isDisabled={addWorkTypeSubmitting}
                    placeholder="Начните вводить или выберите из списка..."
                    noOptionsMessage={() => "Виды работ не найдены"}
                    closeMenuOnSelect={false}
                    menuPortalTarget={selectMenuPortalTarget}
                    styles={selectStylesAddModalMulti}
                  />
                </>
              )}
            </div>
            {catalogSelectionForAdd.length > 0 && (
              <div className="sd-estimate-modal-field">
                <label>
                  Статья учёта по каждому виду (fact_stream_id, Finansist)*
                </label>
                <p className="sd-estimate-add-lines-hint">
                  Для каждой строки откройте поле ниже и выберите статью из
                  выпадающего списка. Если строк много, прокрутите содержимое
                  окна — справа видна полоса прокрутки.
                </p>
                {!workTypeNamesLoading &&
                  finJobTypeSelectOptions.length === 0 && (
                    <div className="sd-estimate-modal-all-added">
                      Справочник статей пуст — без него добавить вид работ нельзя.
                    </div>
                  )}
                <div className="sd-estimate-add-lines">
                  {catalogSelectionForAdd.map((opt, idx) => {
                    const nameKey = String(opt.value);
                    return (
                      <div className="sd-estimate-add-line" key={nameKey}>
                        <div className="sd-estimate-add-line__head">
                          <span className="sd-estimate-add-line__kicker">
                            Вид работ
                          </span>
                          <span
                            className="sd-estimate-add-line__name"
                            title={nameKey}
                          >
                            {opt.label}
                          </span>
                        </div>
                        <label
                          className="sd-estimate-add-line__fin-label"
                          htmlFor={`sd-estimate-fin-job-row-${idx}`}
                        >
                          Статья учёта — выбор из списка
                        </label>
                        <div className="sd-estimate-add-line__select">
                          <Select
                            inputId={`sd-estimate-fin-job-row-${idx}`}
                            options={finJobTypeSelectOptions}
                            value={addWorkTypeLineFinJob[nameKey] ?? null}
                            onChange={(sel) =>
                              setAddWorkTypeLineFinJob((prev) => ({
                                ...prev,
                                [nameKey]: sel,
                              }))
                            }
                            isClearable
                            isDisabled={
                              addWorkTypeSubmitting ||
                              workTypeNamesLoading ||
                              finJobTypeSelectOptions.length === 0
                            }
                            isLoading={workTypeNamesLoading}
                            placeholder="Нажмите и выберите статью из списка…"
                            noOptionsMessage={() => "Статей не найдено"}
                            menuPortalTarget={selectMenuPortalTarget}
                            styles={selectStylesAddModalRowFin}
                            aria-label={`Статья учёта для вида работ: ${nameKey}`}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {isAddWorkTypeCreateNew && (
              <>
                <div className="sd-estimate-modal-field">
                  <label>
                    Введите название вида работ, которого нет в списке
                  </label>
                  <span className="sd-estimate-display-row">
                    <input
                      type="text"
                      className="sd-estimate-modal-input"
                      value={addWorkTypeNewName}
                      onChange={(e) => setAddWorkTypeNewName(e.target.value)}
                      onBlur={validateNewName}
                      placeholder="Введите название вида работ"
                    />
                    <button
                      type="button"
                      className="sd-estimate-pencil"
                      aria-label="Редактировать"
                    >
                      ✎
                    </button>
                  </span>
                  {isDuplicateName && (
                    <p className="sd-estimate-modal-error">
                      <span
                        className="sd-estimate-modal-error-icon"
                        aria-hidden
                      >
                        !
                      </span>
                      Такое название уже существует! Выберите его из списка.
                    </p>
                  )}
                  {isAddWorkTypeCreateNew &&
                    newNameTrimmed &&
                    !isDuplicateName && (
                      <p className="sd-estimate-modal-success">
                        <span
                          className="sd-estimate-modal-success-icon"
                          aria-hidden
                        >
                          ✓
                        </span>
                      </p>
                    )}
                </div>
                <div className="sd-estimate-modal-field">
                  <label>Статья учёта (fact_stream_id, Finansist)*</label>
                  <Select
                    inputId="sd-estimate-fin-job-type-new"
                    options={finJobTypeSelectOptions}
                    value={addWorkTypeFinJobType}
                    onChange={(opt) => setAddWorkTypeFinJobType(opt)}
                    isClearable
                    isDisabled={
                      addWorkTypeSubmitting ||
                      workTypeNamesLoading ||
                      finJobTypeSelectOptions.length === 0
                    }
                    isLoading={workTypeNamesLoading}
                    placeholder="Выберите статью…"
                    noOptionsMessage={() => "Статей не найдено"}
                    menuPortalTarget={selectMenuPortalTarget}
                    styles={selectStylesAddModalSingleFin}
                  />
                  {!workTypeNamesLoading &&
                    finJobTypeSelectOptions.length === 0 && (
                      <div className="sd-estimate-modal-all-added">
                        Справочник статей пуст — без него добавить вид работ
                        нельзя.
                      </div>
                    )}
                </div>
              </>
            )}
            </div>
            <div className="sd-estimate-modal-actions">
              <button
                type="button"
                className="sd-estimate-modal-btn-cancel"
                onClick={closeAddModal}
              >
                Отмена
              </button>
              <button
                type="button"
                className="sd-estimate-modal-btn-add"
                onClick={handleAddWorkType}
                disabled={!canAddWorkType || addWorkTypeSubmitting}
              >
                {addWorkTypeSubmitting ? "Добавление…" : "Добавить"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Блок действий согласующего: Согласовать / Отклонить (если я в списке и статус «на рассмотрении») */}
      {isMyApprovalPending && (
        <div className="sd-estimate-approver-actions">
          <span className="sd-estimate-approver-actions__label">
            Ваше решение по согласованию:
          </span>
          <div className="sd-estimate-approver-actions__buttons">
            <button
              type="button"
              className="sd-estimate-approver-actions__btn sd-estimate-approver-actions__btn--approve"
              onClick={() => handleApproverSetStatus("approved")}
              disabled={isApproverActionLoading}
            >
              {isApproverActionLoading ? "..." : "Согласовать"}
            </button>
            <button
              type="button"
              className="sd-estimate-approver-actions__btn sd-estimate-approver-actions__btn--reject"
              onClick={() => handleApproverSetStatus("rejected")}
              disabled={isApproverActionLoading}
            >
              Отклонить
            </button>
          </div>
        </div>
      )}

      <CommercialOfferApprovalModal
        open={showApprovalModal}
        onClose={() => setShowApprovalModal(false)}
        onSubmit={handleApprovalSubmit}
        projectData={projectData}
        isSubmitting={isSubmittingApproval}
        title="Согласование сметы (ЭП)"
        warningText="Подтвердите отправку сметы на согласование."
        allowAddFromUserList
      />
    </div>
  );
};

export default Estimate;
